import { addSearchParamsToURL } from "@jugl-web/utils";
import { selectIsAuthenticated } from "@web-src/modules/auth/store/authSlice";
import { useAnonymousSession } from "@web-src/modules/auth/providers/AnonymousSessionProvider/useAnonymousSession";
import { useCXSignupDialog } from "@web-src/modules/cx/components/CXSignupDialog/useCXSignupDialog";
import { useEntityProvider } from "@web-src/modules/entities/providers/EntityProvider/useEntityProvider";
import store from "@web-src/store";
import { useCallback } from "react";
import { generatePath, NavigateOptions, useNavigate } from "react-router-dom";
import {
  rootNavigationPageConfig,
  RootPagesNavigationParams,
} from "../root-config";
import { getEntityPagePath } from "../utils/getEntityPagePath";

export type NavigationPage = keyof RootPagesNavigationParams;

export type NavigateFnConfig<TPage extends NavigationPage> = {
  queryParams?: RootPagesNavigationParams[TPage]["queryParams"];
  replacePathOnly?: boolean;
  entityId?: string;
  replace?: NavigateOptions["replace"];
  state?: NavigateOptions["state"];
};

export type ConditionalPageArgs<TPage extends NavigationPage> =
  RootPagesNavigationParams[TPage]["pathParams"] extends never
    ? [undefined?, NavigateFnConfig<TPage>?]
    : [
        RootPagesNavigationParams[TPage]["pathParams"],
        NavigateFnConfig<TPage>?,
      ];

export type PageWithParametersFn<TReturnType> = <TPage extends NavigationPage>(
  page: TPage,
  ...args: ConditionalPageArgs<TPage>
) => TReturnType;

export const useNavigation = () => {
  const { entity } = useEntityProvider();
  const reactRouterNavigate = useNavigate();
  const { isAnonymous } = useAnonymousSession();
  const cxSignupDialog = useCXSignupDialog();

  const generateUrl = useCallback<PageWithParametersFn<string>>(
    (page, ...args) => {
      const [pathParams, params] = args;
      const pageConfig = rootNavigationPageConfig[page];

      let pathToNavigate = generatePath(
        pageConfig.isPrivate && !pageConfig.isEntityOptional
          ? getEntityPagePath(pageConfig.path)
          : pageConfig.path || "",
        {
          ...(pathParams || {}),
          entityId: params?.entityId || entity?.id || null,
        }
      );

      if (params?.queryParams) {
        pathToNavigate = addSearchParamsToURL(
          pathToNavigate,
          params.queryParams
        );
      }

      return pathToNavigate;
    },
    [entity?.id]
  );

  const navigateBack = useCallback(
    () => reactRouterNavigate(-1),
    [reactRouterNavigate]
  );

  const navigate = useCallback<PageWithParametersFn<void>>(
    (page, ...args) => {
      const pageConfig = rootNavigationPageConfig[page];
      const isAuthenticated = selectIsAuthenticated(store.getState());
      if (
        !isAuthenticated &&
        isAnonymous &&
        cxSignupDialog &&
        !pageConfig.isAnonymousAllowed
      ) {
        cxSignupDialog.open();
        return;
      }

      const [, params] = args;
      const url = generateUrl(page, ...args);

      if (params?.replacePathOnly) {
        window.history.pushState(undefined, "", url);
        return;
      }

      reactRouterNavigate(url, {
        replace: params?.replace,
        state: params?.state,
      });
    },
    [isAnonymous, cxSignupDialog, generateUrl, reactRouterNavigate]
  );

  return {
    // Alias is used to differentiate between the two navigate functions
    // while we are refactoring the navigation
    navigateToPage: navigate,
    generateUrl,
    navigateBack,
  };
};
